import { Observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BaseDashboard from "../../../Layouts/Dashboard/BaseDashboard";
import { AuthStore } from "../../../stores/AuthStore";
import { useResizeObserver } from "../../../utils/useResizeObserver";
import { useStore } from "../../../utils/useStore";
import { Routes } from "../../Navigation/Routes";
import {
    agentDashboardDataGridTabs,
    AgentDashboardStore,
    CoachSelectedEvaluationsTask,
} from "./Stores/AgentDashboardStore";
import AgentNavigator from "./Views/AgentNavigator";
import { FormControlLabel, Grid, Tab, Tabs, Typography } from "@mui/material";
import theme from "Theme/AppTheme";
import { AcxTabPanel } from "components/UI/AcxTabPanel";
import AcxSwitch from "components/UI/AcxSwitch";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import { observer } from "mobx-react";
import { AgentCoachingStore } from "../Coaching/Stores/AgentCoachingStore";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { Moment } from "moment";
import {
    DateRangeLabel,
    DateReferenceOption,
} from "stores/ComponentStores/DatePickerComponentStore";
import { CustomControlItem } from "components/UI/AcxDataGrid/AcxDataGridStore";
import DeleteIcon from "@mui/icons-material/Delete";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import AcxButton from "components/UI/AcxButton";
import ConfirmationDialog from "components/UI/AcxConfirmationDialog";
import _ from "lodash";
import CoachingCards from "../Coaching/CoachingCards";
import { agentsDateIntervalOptions } from "../Coaching/AgentCoaching";

export enum AgentDashboardTabs {
    ReadyToCoach = "READY_TO_COACH",
    CompletedCoaching = "COMPLETED_COACHING",
    Evaluations = "EVALUATIONS",
    MyDrafts = "MY_DRAFTS",
    Dashboard = "DASHBOARD",
}

export const agentDashboardTabNames = Object.keys(AgentDashboardTabs).filter(
    (key) => isNaN(Number(key)),
) as (keyof typeof AgentDashboardTabs)[];

export const pendoIdsForAgentDashboardTabs: Record<AgentDashboardTabs, string> =
    {
        [AgentDashboardTabs.ReadyToCoach]: "agent-ready-to-coach-tab",
        [AgentDashboardTabs.CompletedCoaching]: "agent-completed-coaching-tab",
        [AgentDashboardTabs.Evaluations]: "agent-all-evaluations-tab",
        [AgentDashboardTabs.MyDrafts]: "agent-my-drafts-tab",
        [AgentDashboardTabs.Dashboard]: "agent-dashboard-tab",
    };

const referenceOptions = [
    DateReferenceOption.EvaluationCompleteDate,
    DateReferenceOption.InteractionDate,
];

const AgentDashboard = observer(() => {
    const store = useStore(AgentDashboardStore);
    const coachingStore = useStore(AgentCoachingStore);
    const location = useLocation();
    const orgStore = useStore(AuthStore).orgStore;
    const navigate = useNavigate();
    const [segs, setSegs] = useState<string[]>();
    const { elementHeight, elementWidth, theRef } =
        useResizeObserver<HTMLDivElement>();

    // load things on initial navigation to agent dashboard route
    useEffect(() => {
        const parms = location.pathname;
        const seg = parms.split("/");

        setSegs(seg);
        if (seg.length > 0) {
            const orgId = seg[4];
            const agentId = seg[5];
            const agentName = seg[6];

            orgStore.onActiveOrgIdChange(orgId);
            store.setAgentAndOrg(orgId, agentId, agentName);
        } else {
            navigate(Routes.routePrefix + Routes.AGENT_COACHING, {
                replace: true,
            });
        }
    }, [store, orgStore, location.pathname, navigate]);

    // load evals and module scores when filter options change. Drafts reload too —
    // they are filtered on their own Last Modified date against this range.
    useEffect(() => {
        if (store.orgId && store.agentId) {
            store.loadMyDrafts(store.agentId);
            store.loadEvaluationsAndFormat(
                store.orgId,
                store.agentId,
                coachingStore.datePickerStore.beginDate,
                coachingStore.datePickerStore.endDate,
                coachingStore.datePickerStore.referenceOption,
            );
        }
    }, [
        store.orgId,
        store.agentId,
        coachingStore.showMachineEvals,
        coachingStore.datePickerStore.beginDate,
        coachingStore.datePickerStore.endDate,
        coachingStore.datePickerStore.referenceOption,
        store,
    ]);

    // need to build controls for dataGrids
    // TODO-P: should probably refactor the way we handle dg controls entirely as its cumbersome
    useEffect(() => {
        agentDashboardDataGridTabs.forEach((tab) => {
            const dateRangeControl = (
                <Observer>
                    {() => (
                        <AcxDateRangeInput
                            datePickerStore={coachingStore.datePickerStore}
                            displayDateType
                            dateReferenceOptions={referenceOptions}
                            defaultStartDate={
                                coachingStore.datePickerStore.beginDate
                            }
                            defaultEndDate={
                                coachingStore.datePickerStore.endDate
                            }
                            onSelect={(start: Moment, end: Moment) => {
                                coachingStore.datePickerStore.setBeginDate(
                                    start,
                                );
                                coachingStore.datePickerStore.setEndDate(end);
                            }}
                            useCustomDateTypeIds
                            hideLabel
                            sideBar
                            doubleCalendar
                            onDateRangeLabelChange={(
                                dateRangeLabel: DateRangeLabel,
                            ) => {
                                coachingStore.datePickerStore.setDateRangePreset(
                                    dateRangeLabel,
                                );
                            }}
                            dateRangeLabel={
                                coachingStore.datePickerStore
                                    .dateRangePresetLabel
                            }
                            wrapperStyle={{ maxWidth: "420px" }}
                            dateIntervalOptions={agentsDateIntervalOptions}
                        />
                    )}
                </Observer>
            );

            const aiToggle = (
                <Observer>
                    {() => (
                        <FormControlLabel
                            control={
                                <AcxSwitch
                                    onChange={
                                        coachingStore.toggleShowMachineEvals
                                    }
                                    checked={coachingStore.showMachineEvals}
                                />
                            }
                            label="Include AI Scored Evaluations"
                            id="agent-include-ai-scored-evaluations-toggle"
                            sx={{
                                maxHeight: "32px",
                                marginLeft: theme.spacing(2),
                            }}
                        />
                    )}
                </Observer>
            );

            const finalControls: (React.ReactElement | CustomControlItem)[] = [
                aiToggle,
            ];

            finalControls.push(dateRangeControl);

            if (tab === "ReadyToCoach") {
                const coachSelectedButton = (
                    <Observer>
                        {() => (
                            <AcxButton
                                id="agent-ready-to-coach-button"
                                disabled={
                                    !store.dgStoreMap.ReadyToCoach
                                        .selectedRowIds.length
                                }
                                loading={store.getTaskLoading(
                                    CoachSelectedEvaluationsTask,
                                )}
                                onClick={() =>
                                    store.coachSelectedEvaluations(navigate)
                                }
                                startIcon={<FlagRoundedIcon color="disabled" />}
                            >
                                Coach
                            </AcxButton>
                        )}
                    </Observer>
                );

                const selectionSummary = (
                    <Observer>
                        {() => {
                            const selectedCount =
                                store.dgStoreMap.ReadyToCoach.selectedRowIds
                                    .length;
                            if (!selectedCount) return null;

                            return (
                                <Grid
                                    container
                                    alignItems="center"
                                    columnGap={2}
                                    sx={{ mb: 1 }}
                                >
                                    <Grid item>
                                        <Typography
                                            sx={(theme) => ({
                                                color: theme.palette.gray[600],
                                                fontSize: "14px",
                                                fontWeight: 600,
                                            })}
                                        >
                                            {selectedCount} Conversation
                                            {selectedCount === 1
                                                ? ""
                                                : "s"}{" "}
                                            selected
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <AcxButton
                                            variant="text"
                                            color="primary"
                                            onClick={() =>
                                                store.dgStoreMap.ReadyToCoach.clearSelected()
                                            }
                                            sx={{
                                                minWidth: "auto",
                                                padding: 0,
                                            }}
                                        >
                                            Clear
                                        </AcxButton>
                                    </Grid>
                                </Grid>
                            );
                        }}
                    </Observer>
                );

                finalControls.push(coachSelectedButton);
                store.dgStoreMap[tab].preHeader = selectionSummary;
            } else {
                store.dgStoreMap[tab].preHeader = undefined;
            }

            if (tab === "MyDrafts") {
                const deleteDraftsButton = (
                    <Observer>
                        {() => (
                            <AcxButton
                                color="white"
                                disabled={
                                    !store.dgStoreMap.MyDrafts.selectedRowIds
                                        .length
                                }
                                loading={store.getTaskLoading("Delete Drafts")}
                                onClick={() =>
                                    (store.confirmDeleteIsOpen = true)
                                }
                                sx={(theme) => ({
                                    width: "28px",
                                    height: "28px",
                                    minWidth: "0px",
                                    fontSize: "16px",
                                    padding: theme.spacing(1),
                                })}
                            >
                                <DeleteIcon />
                            </AcxButton>
                        )}
                    </Observer>
                );
                finalControls.unshift(deleteDraftsButton);
            }
            store.dgStoreMap[tab].controls = [...finalControls];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChangeTab = (e, value: number) => {
        store.setCurrentTab(value);
    };

    return (
        <Grid container p={2}>
            <Grid item container pb={2}>
                <AgentNavigator currentAgentName={decodeURI(segs?.[6] || "")} />
            </Grid>
            <Grid
                direction="row"
                justifyContent="center"
                xs={12}
                container
                gap={2}
                mb={2}
            >
                <CoachingCards coachingDetails={store.coachingDetails} />
            </Grid>

            <Grid
                borderBottom={"2px solid"}
                borderColor={theme.palette.gray[200]}
                item
                xs={12}
            >
                <Tabs
                    value={store.currentTab}
                    onChange={handleChangeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    {store.tabsByPerm.map((tabName, index) => {
                        return (
                            <Tab
                                key={`${tabName}_${index}`}
                                id={
                                    pendoIdsForAgentDashboardTabs[
                                        AgentDashboardTabs[tabName]
                                    ]
                                }
                                wrapped
                                label={<span>{_.startCase(tabName)}</span>}
                                sx={{
                                    minWidth: "min-content",
                                    textTransform: "none",
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                    paddingRight: "20px",
                                }}
                                style={{ pointerEvents: "auto" }}
                            />
                        );
                    })}
                </Tabs>
            </Grid>
            <Grid container item xs={12}>
                {store.tabsByPerm.map((tab, index) => {
                    return (
                        <AcxTabPanel
                            value={store.currentTab}
                            key={index}
                            index={store.tabsByPerm.findIndex((i) => i === tab)}
                            style={{ width: "100%" }}
                        >
                            <Grid
                                container
                                item
                                xs={12}
                                height={"75vh"}
                                width={"100%"}
                            >
                                {tab !== "Dashboard" ? (
                                    <AcxDataGrid
                                        dataGridStore={store.dgStoreMap[tab]}
                                    />
                                ) : (
                                    <Observer>
                                        {() => (
                                            <div
                                                style={{
                                                    height: "100%",
                                                    position: "relative",
                                                    marginLeft: store
                                                        .dashboardStore
                                                        ?.isWidgetsDrawerOpen
                                                        ? "340px"
                                                        : "0px",
                                                    width: `calc(100% - ${
                                                        store.dashboardStore
                                                            ?.isWidgetsDrawerOpen
                                                            ? "340px"
                                                            : "0px"
                                                    }`,
                                                }}
                                                ref={theRef}
                                            >
                                                {elementWidth &&
                                                    elementHeight &&
                                                    !!store.dashboardStore && (
                                                        <BaseDashboard
                                                            width={
                                                                elementWidth ??
                                                                0
                                                            }
                                                            height={
                                                                elementHeight -
                                                                35
                                                            }
                                                            dashboardStore={
                                                                store.dashboardStore
                                                            }
                                                        />
                                                    )}
                                            </div>
                                        )}
                                    </Observer>
                                )}
                            </Grid>
                        </AcxTabPanel>
                    );
                })}
            </Grid>
            <ConfirmationDialog
                isOpen={store.confirmDeleteIsOpen}
                onClose={() => (store.confirmDeleteIsOpen = false)}
                title={`Delete Selected Drafts`}
                content={
                    <Typography>
                        Are you sure that you would like to delete the selected
                        coaching drafts?
                    </Typography>
                }
                onConfirm={() => store.deleteSelectedDrafts()}
            />
        </Grid>
    );
});

export default AgentDashboard;
